// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>

import type { ClimateData, Station } from '../types';
import { vBars } from '../charts';
import { decadeMeans } from '../analysis';
import { fmtNum, fmtSigned, esc } from '../format';
import { gloss } from '../glossary';
import { openDrawer } from '../drawer';
import { sectionHead, statTile } from './ui';

export function mount(el: HTMLElement, data: ClimateData): void {
  const stationOpts = data.stations.slice().sort((a, b) => a.name.localeCompare(b.name));
  let sel = localStorage.getItem('warm.hotcold.sel') || 'NAT';

  const topHot = data.stations.filter((s) => s.trendH35 != null).slice().sort((a, b) => (b.trendH35 as number) - (a.trendH35 as number)).slice(0, 8);
  const topFrost = data.stations.filter((s) => s.trendFrost != null).slice().sort((a, b) => (a.trendFrost as number) - (b.trendFrost as number)).slice(0, 8);

  el.innerHTML = `
    <section class="panel">
      ${sectionHead('Extremes: hot days and frosty nights', `The warming shows up in the extremes too. ${gloss('hotday', 'Hot days (35 °C+)')} are becoming more common; ${gloss('frost', 'frost nights (≤ 2 °C)')} rarer. Switch between the national average and a single station.`)}
      <div class="controls">
        <label>Show
          <select data-sel>
            <option value="NAT" ${sel === 'NAT' ? 'selected' : ''}>National average (all stations)</option>
            ${stationOpts.map((s) => `<option value="${s.id}" ${sel === s.id ? 'selected' : ''}>${esc(s.name)} (${s.state})</option>`).join('')}
          </select>
        </label>
      </div>
      <div data-tiles class="tiles"></div>
      <h3 class="sub-h">Days at or above 35 °C, per year</h3>
      <div class="chart" data-hot></div>
      <h3 class="sub-h">Frost nights (2 °C or below), per year</h3>
      <div class="chart" data-frost></div>
    </section>

    <div class="two-col">
      <section class="panel">
        ${sectionHead('Biggest rise in hot days', 'Stations gaining 35 °C+ days fastest (days per decade). Click to open.')}
        <div class="lead-list" data-hot-lead>${topHot.map((s, i) => leadRow(s, i, `${fmtSigned(s.trendH35, 1, '')} d/dec`, topHot[0].trendH35 as number, s.trendH35 as number)).join('')}</div>
      </section>
      <section class="panel">
        ${sectionHead('Biggest loss of frosts', 'Stations losing frost nights fastest (nights per decade). Click to open.')}
        <div class="lead-list" data-frost-lead>${topFrost.map((s, i) => leadRow(s, i, `${fmtSigned(s.trendFrost, 1, '')} n/dec`, Math.abs(topFrost[0].trendFrost as number), Math.abs(s.trendFrost as number))).join('')}</div>
      </section>
    </div>`;

  const render = () => {
    const station = sel === 'NAT' ? null : data.stations.find((s) => s.id === sel) || null;
    const rows = station ? station.annual : data.national.annual;
    const hot = rows.filter((r) => r.h35 != null).map((r) => ({ x: r.y, v: r.h35 as number, tip: `${r.y}: ${fmtNum(r.h35)} days ≥ 35 °C` }));
    const frost = rows.filter((r) => r.frost != null).map((r) => ({ x: r.y, v: r.frost as number, tip: `${r.y}: ${fmtNum(r.frost)} frost nights` }));
    (el.querySelector('[data-hot]') as HTMLElement).innerHTML = hot.length ? vBars(hot, { w: 1000, h: 260, color: '#d9614a' }) : '<p class="muted">No hot-day data for this station.</p>';
    (el.querySelector('[data-frost]') as HTMLElement).innerHTML = frost.length ? vBars(frost, { w: 1000, h: 260, color: '#3b6fb0' }) : '<p class="muted">No frost data for this station.</p>';
    const h = decadeMeans(rows as never, 'h35');
    const f = decadeMeans(rows as never, 'frost');
    (el.querySelector('[data-tiles]') as HTMLElement).innerHTML =
      statTile(h.late != null ? `${fmtNum(h.late)} / yr` : '—', 'Hot days now', h.early != null && h.late != null ? `from ${fmtNum(h.early)} in ${h.earlyYears[0]}s` : '') +
      statTile(f.late != null ? `${fmtNum(f.late)} / yr` : '—', 'Frost nights now', f.early != null && f.late != null ? `from ${fmtNum(f.early)} in ${f.earlyYears[0]}s` : '') +
      statTile(station ? `${station.state}` : 'All AU', station ? esc(station.name) : 'National average', station ? `record ${station.startYear}–${station.endYear}` : `${data.meta.stationCount} stations`);
  };

  el.querySelector('[data-sel]')?.addEventListener('change', (e) => {
    sel = (e.target as HTMLSelectElement).value; localStorage.setItem('warm.hotcold.sel', sel); render();
  });
  el.addEventListener('click', (e) => {
    const r = (e.target as Element).closest('[data-station]') as HTMLElement | null;
    if (r) openDrawer(data, r.getAttribute('data-station')!);
  });
  render();
}

function leadRow(s: Station, i: number, val: string, max: number, v: number): string {
  return `<button class="lead-row" data-station="${s.id}">
    <span class="lead-rank">${i + 1}</span>
    <span class="lead-name">${esc(s.name)}<span class="lead-state">${s.state}</span></span>
    <span class="lead-bar-wrap"><span class="lead-bar" style="width:${Math.max(4, (Math.abs(v) / (max || 1)) * 100)}%;"></span></span>
    <span class="lead-val">${val}</span>
  </button>`;
}
